import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useMemo } from 'react';
import { KpiPeriod } from '../../../data-models/company-financials.data-model';
import { GroupedCheckboxPicker, IPickerOption } from './GroupedCheckboxPicker';
import { ReportingPeriodType } from './MultiPeriodReporting.schemas';
import {
  getFormattedConfigPeriod,
  getMprPeriodKey,
  getRelativeDistanceOptions,
  MonthValues,
  MprPeriod,
  YearEndMonth,
  YearValues,
} from './multiPeriodReportingUtils';

export const MprPeriodGroup = {
  rolling: 'Rolling',
  /** The fixed years, when the length is a year; quarters and months group under their year instead. */
  fixed: 'Fixed',
} as const;

/**
 * Every period of one length the picker offers: the rolling ones, then the fixed ones by year, newest
 * first. A selected period outside those lists (an older year, an unusual distance) is offered too, so it
 * can be unchecked.
 */
export function mprPeriodOptions(length: KpiPeriod, selected: MprPeriod[]): IPickerOption<MprPeriod>[] {
  const toOption = (period: MprPeriod, group: string): IPickerOption<MprPeriod> => ({
    key: getMprPeriodKey(period),
    value: period,
    label: getFormattedConfigPeriod(period),
    group,
  });
  const ofLength = selected.filter((period) => period.period === length);

  const distances = new Set(getRelativeDistanceOptions(length).map((option) => option.value));
  ofLength
    .filter((period) => period.type === ReportingPeriodType.relative)
    .forEach((period) => distances.add(period.relativeDistance ?? 0));
  const rolling = [...distances]
    .sort((a, b) => a - b)
    .map((relativeDistance) =>
      toOption(
        { period: length, type: ReportingPeriodType.relative, relativeDistance },
        MprPeriodGroup.rolling
      )
    );

  const years = new Set(YearValues.map((option) => option.value));
  ofLength
    .filter((period) => period.type === ReportingPeriodType.absolute && period.year != null)
    .forEach((period) => years.add(period.year!));
  const months =
    length === KpiPeriod.year ? [YearEndMonth] : MonthValues[length].map((option) => option.value);
  const fixed = [...years]
    .sort((a, b) => b - a)
    .flatMap((year) =>
      months.map((month) =>
        toOption(
          { period: length, type: ReportingPeriodType.absolute, year, month },
          length === KpiPeriod.year ? MprPeriodGroup.fixed : String(year)
        )
      )
    );
  return [...rolling, ...fixed];
}

/** The length the picker opens on: that of the last period in the list, the one a new period would follow. */
export function lastMprPeriodLength(periods: MprPeriod[]): KpiPeriod {
  return (periods.at(-1)?.period as KpiPeriod | undefined) ?? KpiPeriod.quarter;
}

interface IMprPeriodPickerProps {
  label: string;
  /** The report's periods, in order; the picker's checked options. */
  selected: MprPeriod[];
  onChange: (periods: MprPeriod[]) => void;
  /** Which periods are listed: months, quarters or years. */
  length: KpiPeriod;
  onLengthChange: (length: KpiPeriod) => void;
}

/** A checkbox multi-select over rolling and fixed periods of one length; its checked state is the period list. */
export function MprPeriodPicker({
  label,
  selected,
  onChange,
  length,
  onLengthChange,
}: IMprPeriodPickerProps) {
  const options = useMemo(() => mprPeriodOptions(length, selected), [length, selected]);

  return (
    <GroupedCheckboxPicker
      label={label}
      options={options}
      selected={selected}
      keyOf={getMprPeriodKey}
      onChange={onChange}
      heading={
        <ToggleButtonGroup
          aria-label='Period length'
          color='secondary'
          size='small'
          exclusive
          value={length}
          onChange={(_, value: KpiPeriod | null) => {
            if (value != null) {
              onLengthChange(value);
            }
          }}
        >
          <ToggleButton value={KpiPeriod.month}>Month</ToggleButton>
          <ToggleButton value={KpiPeriod.quarter}>Quarter</ToggleButton>
          <ToggleButton value={KpiPeriod.year}>Year</ToggleButton>
        </ToggleButtonGroup>
      }
    />
  );
}
